import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Mail, Lock, Loader2, AlertCircle } from 'lucide-react';
import Logo from '../components/Logo';
import { isNativePlatform, getBackendUrl } from '../lib/platform';

// Build-time mobile flag (replaced at compile time by CRA)
const IS_MOBILE_BUILD = process.env.REACT_APP_PLATFORM === 'mobile';
const PROD_API = 'https://www.10minvestor.com/api';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorDetail, setErrorDetail] = useState(null);
  const [apiReachable, setApiReachable] = useState(null);
  const [debugInfo, setDebugInfo] = useState({});
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Collect debug info for Apple review screenshots
    const info = {
      buildFlag: IS_MOBILE_BUILD ? 'mobile' : 'web',
      runtimeNative: isNativePlatform(),
      apiUrl: getBackendUrl(),
      hostname: window.location?.hostname || 'unknown',
      protocol: window.location?.protocol || 'unknown',
      capacitorObj: typeof window.Capacitor !== 'undefined' ? 'present' : 'absent',
      userAgent: (navigator.userAgent || '').substring(0, 80),
    };
    setDebugInfo(info);

    const checkApi = async () => {
      try {
        const healthUrl = isNativePlatform() ? `${PROD_API}/health` : `${window.location.origin}/api/health`;
        const resp = await fetch(healthUrl, { method: 'GET' });
        setApiReachable(resp.ok);
        setDebugInfo(prev => ({ ...prev, healthStatus: resp.ok ? 'OK' : `HTTP ${resp.status}`, healthUrl }));
      } catch (err) {
        setApiReachable(false);
        setDebugInfo(prev => ({ ...prev, healthStatus: `FAIL: ${err.message}`, healthUrl: isNativePlatform() ? `${PROD_API}/health` : `${window.location.origin}/api/health` }));
      }
    };
    checkApi();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorDetail(null);

    try {
      await login(email, password);
      toast.success('Connexion réussie !');
      navigate('/dashboard');
    } catch (error) {
      let msg;
      if (error.response) {
        msg = error.response.data?.detail || `Erreur serveur (${error.response.status})`;
      } else if (error.request) {
        msg = 'Impossible de joindre le serveur. Vérifiez votre connexion internet.';
      } else {
        msg = error.message || 'Erreur inattendue';
      }
      setErrorDetail(msg);
      setDebugInfo(prev => ({ ...prev, lastError: msg, errorType: error.response ? 'response' : error.request ? 'network' : 'unknown' }));
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="mb-8">
          <Logo size="xxlarge" className="hidden md:flex" />
          <Logo variant="vertical" size="xlarge" className="flex md:hidden" />
        </div>
        <Card className="w-full max-w-md border-slate-200 shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-slate-900">Connexion</CardTitle>
            <CardDescription>Accédez à votre espace investisseur</CardDescription>
          </CardHeader>
          <CardContent>
            {apiReachable === false && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="api-unreachable-warning">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">Le serveur est temporairement inaccessible. Veuillez réessayer dans quelques instants.</p>
              </div>
            )}

            {errorDetail && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="login-error-detail">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorDetail}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    data-testid="login-email-input"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    data-testid="login-password-input"
                    required
                  />
                </div>
              </div>
              
              <div className="text-right">
                <Link to="/forgot-password" className="text-sm text-[#0D47A1] hover:underline">
                  Mot de passe oublié ?
                </Link>
              </div>
              
              <Button
                type="submit"
                className="w-full bg-[#0D47A1] hover:bg-[#0A367A]"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Connexion...
                  </>
                ) : (
                  'Se connecter'
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-slate-600">
              Pas encore de compte ?{' '}
              <Link to="/register" className="font-medium text-[#0D47A1] hover:underline">
                Créer un compte
              </Link>
            </p>
          </CardContent>
        </Card>

        {/* Debug panel - visible on ALL builds for Apple review diagnosis */}
        {(IS_MOBILE_BUILD || isNativePlatform()) && (
          <div className="w-full max-w-md mt-4 p-3 bg-slate-800 rounded-lg text-xs font-mono text-slate-300" data-testid="debug-panel">
            <p className="text-slate-500 mb-1">Build v1.2 Debug</p>
            <p>Platform: <span className={debugInfo.buildFlag === 'mobile' ? 'text-green-400' : 'text-red-400'}>{debugInfo.buildFlag}</span></p>
            <p>Native: <span className={debugInfo.runtimeNative ? 'text-green-400' : 'text-red-400'}>{String(debugInfo.runtimeNative)}</span></p>
            <p>API: {debugInfo.apiUrl || 'N/A'}</p>
            <p>Health: <span className={debugInfo.healthStatus === 'OK' ? 'text-green-400' : 'text-yellow-400'}>{debugInfo.healthStatus || 'checking...'}</span></p>
            <p>Host: {debugInfo.hostname}</p>
            <p>Proto: {debugInfo.protocol}</p>
            <p>Cap: {debugInfo.capacitorObj}</p>
            {debugInfo.lastError && <p className="text-red-400 mt-1">Err: {debugInfo.lastError}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
